import { classes, events } from '@theme/constants';
import { getRefs, parseHtml } from '@theme/dom';
import { fetchConfig, json, text } from '@theme/fetch';
import { HTMLShopifySectionElement } from '@theme/html-custom-element';

class CartRecommendations extends HTMLShopifySectionElement {
  productId = null;
  isCollection = false;

  async connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.handleCartChange();

    /**
     * Refresh the recommendations when the cart updates.
     */
    this.subscribe(events.sectionUpdate, (event) => {
      const section = event.sections[this.getAttribute('section-id')];

      if (section) {
        this.handleCartChange(parseHtml(section, this.tagName.toLowerCase()));
      }
    });
  }

  /**
   * Handles the cart change event.
   * @param {CartRecommendations} element - The incoming cart recommendations element.
   */
  handleCartChange(element = this) {
    const refs = getRefs(element);

    if (!refs) return;

    /**
     * If there aren't any static collection products, fetch the dynamic recommendations.
     */
    if (refs.products.children.length === 0) {
      this.fetchRecommendations();
      return;
    }

    if (!this.isCollection) {
      this.refs.products.innerHTML = refs.products.innerHTML;
      this.refs.products.scrollLeft = 0;
      this.productId = null;
      this.isCollection = true;

      requestAnimationFrame(() => {
        this.classList.toggle(classes.active, this.refs.products.children.length);
      });
    }
  }

  /**
   * Fetches and builds the product recommendations.
   */
  async fetchRecommendations() {
    const {
      item_count,
      items: [item],
    } = await this.getCart();

    this.classList.toggle(classes.active, !(item_count === 0));

    if (item_count === 0 || (item && this.productId === item.product_id)) return;

    const product = await text(
      fetch(
        `${theme.routes.product_recommendations_url}?product_id=${item.product_id}&section_id=cart-recommendations`,
      ),
    );

    const section = parseHtml(product);
    const recommendations = section.getElementById('shopify-section-cart-recommendations');

    this.refs.products.innerHTML = recommendations.innerHTML;
    this.refs.products.scrollLeft = 0;
    this.productId = item.product_id;
    this.isCollection = false;

    this.classList.toggle(classes.active, this.refs.products.children.length);
  }

  /**
   * Returns a cart promise.
   * @returns {Promise<object>}
   */
  getCart() {
    return json(fetch(theme.routes.cart_url, fetchConfig('json', { method: 'GET' })));
  }
}

if (!customElements.get('cart-recommendations')) customElements.define('cart-recommendations', CartRecommendations);
